import React, { useState } from "react";
import SortableList from "./SortableList";
import SectionEditor from "./SectionEditor";
import WeightMeter from "./WeightMeter";
import {
  SECTION_TYPES,
  newSection,
  sectionWeightTotal,
} from "../templateUtils";

/** Ordered sections with the "Section weights: x / 100" meter and an add-section picker. */
const SectionsEditor = ({ sections, onChange, readOnly }) => {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [picking, setPicking] = useState(false);
  const hasRating = sections.some((s) => s.type === "RATING");

  const toggle = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const add = (type) => {
    onChange([...sections, newSection(type)]);
    setPicking(false);
  };

  return (
    <section className="builder-card" aria-labelledby="sections-title">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h2 id="sections-title" className="builder-card-title m-0">
          <i className="bi bi-layout-text-sidebar"></i> Sections
        </h2>
        {sections.length > 1 && (
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-link text-slate-600 p-0"
              onClick={() => setCollapsed(new Set(sections.map((s) => s._id)))}
            >
              Collapse all
            </button>
            <button
              type="button"
              className="btn btn-sm btn-link text-slate-600 p-0"
              onClick={() => setCollapsed(new Set())}
            >
              Expand all
            </button>
          </div>
        )}
      </div>

      {hasRating && (
        <div className="mb-3">
          <WeightMeter
            label="Section weights (rating sections)"
            total={sectionWeightTotal(sections)}
          />
        </div>
      )}

      {sections.length === 0 && (
        <div className="builder-empty">
          No sections yet. Add a rating section to start building the form.
        </div>
      )}

      <SortableList
        items={sections}
        onReorder={onChange}
        readOnly={readOnly}
        renderItem={(section, index, { handle, moveUp, moveDown }) => (
          <SectionEditor
            sections={sections}
            index={index}
            onChange={(next) =>
              onChange(sections.map((s, i) => (i === index ? next : s)))
            }
            onRemove={() => {
              if (
                window.confirm(`Remove "${section.title || "this section"}"?`)
              )
                onChange(sections.filter((_, i) => i !== index));
            }}
            collapsed={collapsed.has(section._id)}
            onToggle={() => toggle(section._id)}
            handle={handle}
            moveUp={moveUp}
            moveDown={moveDown}
            readOnly={readOnly}
          />
        )}
      />

      {!readOnly &&
        (picking ? (
          <div
            className="section-type-picker"
            role="group"
            aria-label="Choose section type"
          >
            {SECTION_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                className="section-type-option"
                onClick={() => add(t.value)}
              >
                <i className={`bi ${t.icon}`}></i>
                <span className="fw-semibold">{t.label}</span>
                <span className="text-slate-500 fs-075">{t.hint}</span>
              </button>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-link text-slate-600"
              onClick={() => setPicking(false)}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-outline-primary w-100 mt-2 builder-add"
            onClick={() => setPicking(true)}
          >
            <i className="bi bi-plus-lg"></i> Add section
          </button>
        ))}
    </section>
  );
};

export default SectionsEditor;
